import React, { useState, useEffect, useRef } from "react";

import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Compressor from "compressorjs";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import { BACKEND_URL } from "../../config/env";
import AudioRecorder from "../AudioRecorder";
import CustomToggleSwitch from "../CustomToggleSwitch";
import AudioModal from "../AudioModal";
import {
  Stack,
  Typography,
} from "@mui/material";
import {
  Bot,
  Braces,
  Clock3,
  FileText,
  Image as ImageIcon,
  MessageSquareText,
  Mic,
  TimerReset,
  Trash2,
  Video,
} from "lucide-react";
import { Box } from "@material-ui/core";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";

  return ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialogPaper: {
    ...(theme.chatiaDS?.dialogPaper || {}),
    "& .MuiDialogContent-root": {
      flex: 1,
      overflowY: "auto !important",
      overflowX: "hidden",
      background: isDark ? "#121212" : "#ffffff",
      ...theme.scrollbarStyles,
    },
    "& .MuiDialogActions-root": {
      flexShrink: 0,
      padding: "12px 16px",
      background: isDark ? "#121212" : "#ffffff",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
    },
  },
  header: {
    ...(theme.chatiaDS?.header || {}),
    flexShrink: 0,
  },
  headerTitle: {
    ...(theme.chatiaDS?.headerTitle || {}),
  },
  closeButton: {
    backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
    color: theme.chatiaDS?.closeButton?.color || "#ffffff",
    transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
      color: "#ffffff",
    },
  },
  content: {
    padding: "18px 20px !important",
    maxHeight: "70vh",
  },
  bodyCard: {
    gap: 12,
    scrollBehavior: "smooth",
    display: "flex",
    flexDirection: "column",
  },
  card: {
    background: theme.mode === "dark" ? "#16191f" : "#ffffff",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    borderRadius: 12,
    padding: 16,
    position: "relative",
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    transition: "border-color 160ms ease, background-color 160ms ease, transform 160ms ease",
    "&:hover": {
      background: theme.mode === "dark" ? "#1a1e26" : "#f7f8fa",
      borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.14)" : "rgba(15, 23, 42, 0.14)",
      transform: "translateY(-1px)",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.1,
  },
  cardIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    color: theme.palette.primary.main,
    "& svg": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  deleteButton: {
    position: "absolute",
    right: 10,
    top: 10,
    color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    padding: 5,
    borderRadius: 8,
    "&:hover": {
      color: "#ffffff",
      background: "rgba(255,255,255,0.08)",
    },
  },
  uploadButton: {
    width: "100%",
    borderRadius: "10px !important",
    textTransform: "none",
    fontWeight: 700,
    background: theme.mode === "dark" ? "#1d222b !important" : "#f7f8fa !important",
    color: theme.mode === "dark" ? "#e9edef !important" : "#111b21 !important",
    boxShadow: "none !important",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.10)" : "1px solid rgba(15, 23, 42, 0.10)",
    padding: "9px 12px !important",
    "&:hover": {
      background: theme.mode === "dark" ? "#242a35 !important" : "#eef1f4 !important",
      borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.16)" : "rgba(15, 23, 42, 0.16)",
      boxShadow: "none !important",
    },
    "& .MuiButton-startIcon": {
      color: theme.palette.primary.main,
    },
    "& .MuiButton-startIcon svg": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  audioPreview: {
    display: "flex",
    justifyContent: "center",
    "& audio": {
      width: "100%",
      maxWidth: 520,
    },
  },
  audioOption: {
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0 6px",
    background: "transparent",
    border: 0,
    color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    "& .MuiTypography-root": {
      fontSize: 13,
      fontWeight: 600,
    },
  },
  actionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(142px, 1fr))",
    gap: 10,
  },
  addButton: {
    justifyContent: "flex-start",
    borderRadius: "12px !important",
    textTransform: "none",
    fontWeight: 700,
    color: theme.mode === "dark" ? "#e9edef !important" : "#111b21 !important",
    background: theme.mode === "dark" ? "#16191f !important" : "#ffffff !important",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    boxShadow: "none !important",
    minHeight: 42,
    padding: "8px 12px !important",
    transition: "border-color 160ms ease, background-color 160ms ease, transform 160ms ease",
    "&:hover": {
      background: theme.mode === "dark" ? "#1d222b !important" : "#f7f8fa !important",
      borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.14)" : "rgba(15, 23, 42, 0.14)",
      boxShadow: "none !important",
      transform: "translateY(-1px)",
    },
  },
  infoCard: {
    background: theme.mode === "dark" ? "#16191f" : "#ffffff",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
  },
  variablesList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  variablePill: {
    borderRadius: 9,
    padding: "6px 9px",
    fontSize: 12,
    fontFamily: "monospace",
    background: theme.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  });
});

const FlowBuilderSingleBlockModal = ({
  open,
  onSave,
  onUpdate,
  data,
  close,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [medias, setMedias] = useState([]);
  const [elements, setElements] = useState([]);
  const [elementsSeq, setElementsSeq] = useState([]);
  const [elementsSeqEdit, setElementsSeqEdit] = useState([]);
  const [elementsEdit, setElementsEdit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState([]);

  const [numberMessages, setNumberMessages] = useState(0);
  const [numberMessagesLast, setNumberMessagesLast] = useState(0);

  const [numberInterval, setNumberInterval] = useState(0);
  const [numberIntervalLast, setNumberIntervalLast] = useState(0);
  
  const [numberImg, setNumberImg] = useState(0);
  const [numberImgLast, setNumberImgLast] = useState(0);
  const [previewImg, setPreviewImg] = useState([]);
  
  const [numberAudio, setNumberAudio] = useState(0);
  const [numberAudioLast, setNumberAudioLast] = useState(0);
  const [previewAudios, setPreviewAudios] = useState([]);
  
  const [numberVideo, setNumberVideo] = useState(0);
  const [numberVideoLast, setNumberVideoLast] = useState(0);
  const [previewVideos, setPreviewVideos] = useState([]);

  const [numberDocument, setNumberDocument] = useState(0);
  const [numberDocumentLast, setNumberDocumentLast] = useState(0);
  const [previewDocuments, setPreviewDocuments] = useState([]);
  const [timeoutSeconds, setTimeoutSeconds] = useState(0);
  const [aiTimeoutSeconds, setAiTimeoutSeconds] = useState(0);

  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilderModals.singleBlockModal.titleAdd"),
    btn: i18n.t("flowBuilderModals.singleBlockModal.buttonAdd"),
  });

  const handleElements = (newNameFiles) => {
    let elementsSequence = [];

    const newArrMessage = elementsSeq.filter((item) => item.includes("message"));
    const newArrInterval = elementsSeq.filter((item) => item.includes("interval"));
    const newArrImg = elementsSeq.filter((item) => item.includes("img"));
    const newArrAudio = elementsSeq.filter((item) => item.includes("audio"));
    const newArrVideo = elementsSeq.filter((item) => item.includes("video"));
    const newArrDocument = elementsSeq.filter((item) => item.includes("document"));

    for (let i = 0; i < numberMessages; i++) {
      const value = document.querySelector(`.${newArrMessage[i]}`).querySelector(".MuiInputBase-input").value;
      if (!value) {
        toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.emptyMessageFields"));
        setLoading(false);
        throw "";
      }
      elementsSequence.push({
        type: "message",
        value: value,
        number: newArrMessage[i],
      });
    }

    for (let i = 0; i < numberInterval; i++) {
      const value = document.querySelector(`.${newArrInterval[i]}`).querySelector(".MuiInputBase-input").value;
      if (parseInt(value) === 0 || parseInt(value) > 120) {
        toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.intervalValidation"));
        setLoading(false);
        throw "";
      }
      elementsSequence.push({
        type: "interval",
        value: value,
        number: newArrInterval[i],
      });
    }

    for (let i = 0; i < numberImg; i++) {
      const onlyImg = newNameFiles !== null && newNameFiles.filter((file) => file.includes("png") || file.includes("jpg") || file.includes("jpeg"));
      const onlyImgNameOriginal = medias.filter((file) => file.name.includes("png") || file.name.includes("jpg") || file.name.includes("jpeg"));
      if (elementsSeqEdit.includes(newArrImg[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrImg[i])[0];
        elementsSequence.push({ type: "img", value: itemSelectedEdit.value, original: itemSelectedEdit.original, number: itemSelectedEdit.number });
      } else {
        let indexElem = elementsSeqEdit.filter((item) => item.includes("img")).length > 0 ? elementsSeqEdit.filter((item) => item.includes("img")).length - i : i;
        elementsSequence.push({ type: "img", value: onlyImg[indexElem], original: onlyImgNameOriginal[indexElem].name, number: newArrImg[i] });
      }
    }

    for (let i = 0; i < numberAudio; i++) {
      const onlyAudio = newNameFiles !== null && newNameFiles.filter((file) => file.includes("mp3") || file.includes("ogg") || file.includes("mpeg") || file.includes("opus"));
      const onlyAudioNameOriginal = medias.filter((file) => file.name.includes("mp3") || file.name.includes("ogg") || file.name.includes("mpeg") || file.name.includes("opus"));
      if (elementsSeqEdit.includes(newArrAudio[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrAudio[i])[0];
        elementsSequence.push({ type: "audio", value: itemSelectedEdit.value, original: itemSelectedEdit.original, number: itemSelectedEdit.number, record: document.querySelector(`.check${newArrAudio[i]}`).querySelector("input").checked });
      } else {
        let indexElem = elementsSeqEdit.filter((item) => item.includes("audio")).length > 0 ? elementsSeqEdit.filter((item) => item.includes("audio")).length - i : i;
        elementsSequence.push({ type: "audio", value: onlyAudio[indexElem], original: onlyAudioNameOriginal[indexElem].name, number: newArrAudio[i], record: document.querySelector(`.check${newArrAudio[i]}`).querySelector("input").checked });
      }
    }

    for (let i = 0; i < numberVideo; i++) {
      const onlyVideo = newNameFiles !== null && newNameFiles.filter((file) => file.includes("mp4") || file.includes("avi"));
      const onlyVideoNameOriginal = medias.filter((file) => file.name.includes("mp4") || file.name.includes("avi"));
      if (elementsSeqEdit.includes(newArrVideo[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrVideo[i])[0];
        elementsSequence.push({ type: "video", value: itemSelectedEdit.value, original: itemSelectedEdit.original, number: itemSelectedEdit.number });
      } else {
        let indexElem = elementsSeqEdit.filter((item) => item.includes("video")).length > 0 ? elementsSeqEdit.filter((item) => item.includes("video")).length - i : i;
        elementsSequence.push({ type: "video", value: onlyVideo[indexElem], original: onlyVideoNameOriginal[indexElem].name, number: newArrVideo[i] });
      }
    }

    for (let i = 0; i < numberDocument; i++) {
      const onlyDocument = newNameFiles !== null && newNameFiles.filter((file) => file.includes("pdf") || file.includes("doc") || file.includes("docx") || file.includes("xls") || file.includes("xlsx") || file.includes("txt") || file.includes("csv"));
      const onlyDocumentNameOriginal = medias.filter((file) => file.name.includes("pdf") || file.name.includes("doc") || file.name.includes("docx") || file.name.includes("xls") || file.name.includes("xlsx") || file.name.includes("txt") || file.name.includes("csv"));
      if (elementsSeqEdit.includes(newArrDocument[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrDocument[i])[0];
        elementsSequence.push({ type: "document", value: itemSelectedEdit.value, original: itemSelectedEdit.original, number: itemSelectedEdit.number });
      } else {
        let indexElem = elementsSeqEdit.filter((item) => item.includes("document")).length > 0 ? elementsSeqEdit.filter((item) => item.includes("document")).length - i : i;
        elementsSequence.push({ type: "document", value: onlyDocument[indexElem], original: onlyDocumentNameOriginal[indexElem].name, number: newArrDocument[i] });
      }
    }

    return elementsSequence;
  };

  const deleteElementsTypeOne = (id, type) => {
    if (type === "message") {
      setNumberMessages((old) => old - 1);
      setElementsSeq((old) => old.filter((item) => item !== `message${id}`));
      setElementsSeqEdit((old) => old.filter((item) => item !== `message${id}`));
      document.querySelector(`.stackMessage${id}`).remove();
    }
    if (type === "interval") {
      setNumberInterval((old) => old - 1);
      setElementsSeq((old) => old.filter((item) => item !== `interval${id}`));
      setElementsSeqEdit((old) => old.filter((item) => item !== `interval${id}`));
      document.querySelector(`.stackInterval${id}`).remove();
    }
    if (type === "img") {
      setNumberImg((old) => old - 1);
      setPreviewImg((old) => {
        setMedias((oldMedia) => {
          try {
            return oldMedia.filter((mediaItem) => mediaItem.name !== old.filter((item) => item.number === id)[0].name);
          } catch (e) {
            return oldMedia;
          }
        });
        return old.filter((item) => item.number !== id);
      });
      setElementsSeq((old) => old.filter((item) => item !== `img${id}`));
      setElementsSeqEdit((old) => old.filter((item) => item !== `img${id}`));
      document.querySelector(`.stackImg${id}`).remove();
    }
    if (type === "audio") {
      setNumberAudio((old) => old - 1);
      setPreviewAudios((old) => {
        setMedias((oldMedia) => {
          try {
            return oldMedia.filter((mediaItem) => mediaItem.name !== old.filter((item) => item.number === id)[0].name);
          } catch (e) {
            return oldMedia;
          }
        });
        return old.filter((item) => item.number !== id);
      });
      setElementsSeq((old) => old.filter((item) => item !== `audio${id}`));
      setElementsSeqEdit((old) => old.filter((item) => item !== `audio${id}`));
      document.querySelector(`.stackAudio${id}`).remove();
    }
    if (type === "video") {
      setNumberVideo((old) => old - 1);
      setPreviewVideos((old) => {
        setMedias((oldMedia) => {
          try {
            return oldMedia.filter((mediaItem) => mediaItem.name !== old.filter((item) => item.number === id)[0].name);
          } catch (e) {
            return oldMedia;
          }
        });
        return old.filter((item) => item.number !== id);
      });
      setElementsSeq((old) => old.filter((item) => item !== `video${id}`));
      setElementsSeqEdit((old) => old.filter((item) => item !== `video${id}`));
      document.querySelector(`.stackVideo${id}`).remove();
    }
    if (type === "document") {
      setNumberDocument((old) => old - 1);
      setPreviewDocuments((old) => {
        setMedias((oldMedia) => {
          try {
            return oldMedia.filter((mediaItem) => mediaItem.name !== old.filter((item) => item.number === id)[0].name);
          } catch (e) {
            return oldMedia;
          }
        });
        return old.filter((item) => item.number !== id);
      });
      setElementsSeq((old) => old.filter((item) => item !== `document${id}`));
      setElementsSeqEdit((old) => old.filter((item) => item !== `document${id}`));
      document.querySelector(`.stackDocument${id}`).remove();
    }
  };
  
  const handleChangeMediasImg = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 2000000) {
      toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.fileTooLarge2MB"));
      return;
    }
    const imgBlob = URL.createObjectURL(e.target.files[0]);
    setPreviewImg((old) => [...old, { number: number, url: imgBlob, name: e.target.files[0].name }]);
    setElementsSeqEdit(old => old.filter(item => item !== `img${number}`));
    const selectedMedias = Array.from(e.target.files);
    setMedias((old) => [...old, selectedMedias[0]]);
    document.querySelector(`.img${number}`).src = imgBlob;
  };

  const handleChangeAudios = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 5000000) {
      toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.fileTooLarge5MB"));
      return;
    }
    const audioBlob = URL.createObjectURL(e.target.files[0]);
    setPreviewAudios((old) => [...old, { number: number, url: audioBlob, name: e.target.files[0].name }]);
    setElementsSeqEdit(old => old.filter(item => item !== `audio${number}`));
    const selectedMedias = Array.from(e.target.files);
    setMedias((old) => [...old, selectedMedias[0]]);
    setElements((old) => old.map((item) => item.key === `stackAudio${number}` ? audioLayout(number, "", "", audioBlob) : item));
  };

  const handleRecordedAudio = (blob, file, number) => {
    const audioBlob = URL.createObjectURL(blob);
    setPreviewAudios((old) => [...old, { number: number, url: audioBlob, name: file.name }]);
    setElementsSeqEdit(old => old.filter(item => item !== `audio${number}`));
    setMedias((old) => [...old, file]);
    setElements((old) => old.map((item) => item.key === `stackAudio${number}` ? audioLayout(number, "", "", audioBlob) : item));
    // Auto-check "send as recorded audio"
    const checkInput = document.querySelector(`.checkaudio${number} input`);
    if (checkInput && !checkInput.checked) checkInput.click();
  };

  const handleChangeVideos = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 20000000) {
      toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.fileTooLarge20MB"));
      return;
    }
    const videoBlob = URL.createObjectURL(e.target.files[0]);
    setPreviewVideos((old) => [...old, { number: number, url: videoBlob, name: e.target.files[0].name }]);
    setElementsSeqEdit(old => old.filter(item => item !== `video${number}`));
    var divConteudo = document.createElement("div");
    const selectedMedias = Array.from(e.target.files);
    setMedias((old) => [...old, selectedMedias[0]]);
    divConteudo.innerHTML = `<video controls style="width: 200px;"><source src="${videoBlob}" type="video/mp4" />${i18n.t("flowBuilderModals.singleBlockModal.validation.browserNotSupported")}</video>`;
    document.querySelector(`.video${number}`).appendChild(divConteudo);
  };

  const handleChangeDocuments = (e, number) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    if (file.size > 15000000) {
      toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.fileTooLarge15MB"));
      return;
    }
    setPreviewDocuments((old) => [...old, { number: number, name: file.name }]);
    setElementsSeqEdit(old => old.filter(item => item !== `document${number}`));
    const selectedMedias = Array.from(e.target.files);
    setMedias((old) => [...old, selectedMedias[0]]);
    document.querySelector(`.document${number}`).textContent = file.name;
  };

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  const DeleteCardButton = ({ onClick }) => (
    <IconButton size="small" className={classes.deleteButton} onClick={onClick}>
      <Trash2 size={16} />
    </IconButton>
  );
  
  const imgLayout = (number, valueDefault = "") => (
    <Stack className={`stackImg${number} ${classes.card}`} key={`stackImg${number}`}>
      <DeleteCardButton onClick={() => deleteElementsTypeOne(number, "img")} />
      <CardTitle icon={ImageIcon}>{i18n.t("flowBuilderModals.singleBlockModal.elements.image")}</CardTitle>
      <Stack direction={"row"} justifyContent={"center"}><img src={valueDefault.length > 0 ? BACKEND_URL + "/public/" + valueDefault : ""} className={`img${number}`} style={{ width: "200px", maxWidth: "100%", borderRadius: 8 }} alt="" /></Stack>
      <Button variant="contained" component="label" className={`${classes.uploadButton} btnImg${number}`}>{i18n.t("flowBuilderModals.singleBlockModal.buttons.sendImage")}<input type="file" accept="image/png, image/jpg, image/jpeg" hidden onChange={(e) => handleChangeMediasImg(e, number)} /></Button>
    </Stack>
  );

  const audioLayout = (number, valueDefault = "", valueRecordDefault = "", previewUrl = "") => (
    <Stack className={`stackAudio${number} ${classes.card}`} key={`stackAudio${number}`}>
      <DeleteCardButton onClick={() => deleteElementsTypeOne(number, "audio")} />
      <CardTitle icon={Mic}>{i18n.t("flowBuilderModals.singleBlockModal.elements.audio")}</CardTitle>
      <div className={`audio${number} ${classes.audioPreview}`}>
        {(previewUrl || valueDefault.length > 0) && (
          <AudioModal url={previewUrl || `${BACKEND_URL}/public/${valueDefault}`} fullWidth />
        )}
      </div>
      <Button variant="contained" component="label" className={`${classes.uploadButton} btnAudio${number}`}>{i18n.t("flowBuilderModals.singleBlockModal.buttons.sendAudio")}<input type="file" accept="audio/ogg, audio/mp3, audio/opus" hidden onChange={(e) => handleChangeAudios(e, number)} /></Button>
      <AudioRecorder onRecorded={(blob, file) => handleRecordedAudio(blob, file, number)} />
      <Stack direction={"row"} className={classes.audioOption}>
        <Typography>{i18n.t("flowBuilderModals.singleBlockModal.fields.sendAsRecordedAudio")}</Typography>
        <CustomToggleSwitch className={`checkaudio${number}`} defaultChecked={valueRecordDefault === "ok" ? false : true} />
      </Stack>
    </Stack>
  );

  const videoLayout = (number, valueDefault = "") => (
    <Stack className={`stackVideo${number} ${classes.card}`} key={`stackVideo${number}`}>
      <DeleteCardButton onClick={() => deleteElementsTypeOne(number, "video")} />
      <CardTitle icon={Video}>{i18n.t("flowBuilderModals.singleBlockModal.elements.video")}</CardTitle>
      <div className={`video${number}`} style={{ display: "flex", justifyContent: "center" }}>{valueDefault.length > 0 && (<video controls style={{ width: "200px" }}><source src={BACKEND_URL + "/public/" + valueDefault} type="video/mp4" />{i18n.t("flowBuilderModals.singleBlockModal.validation.browserNotSupported")}</video>)}</div>
      <Button variant="contained" component="label" className={`${classes.uploadButton} btnVideo${number}`}>{i18n.t("flowBuilderModals.singleBlockModal.buttons.sendVideo")}<input type="file" accept="video/mp4" hidden onChange={(e) => handleChangeVideos(e, number)} /></Button>
    </Stack>
  );

  const documentLayout = (number, valueDefault = "") => (
    <Stack className={`stackDocument${number} ${classes.card}`} key={`stackDocument${number}`}>
      <DeleteCardButton onClick={() => deleteElementsTypeOne(number, "document")} />
      <CardTitle icon={FileText}>{i18n.t("flowBuilderModals.singleBlockModal.elements.document")}</CardTitle>
      <Stack direction={"row"} justifyContent={"center"} alignItems="center" gap={1} my={1}><FileText size={18} /><Typography className={`document${number}`}>{valueDefault.length > 0 ? valueDefault : i18n.t("flowBuilderModals.singleBlockModal.fields.noFileSelected")}</Typography></Stack>
      <Button variant="contained" component="label" className={`${classes.uploadButton} btnDocument${number}`}>{i18n.t("flowBuilderModals.singleBlockModal.buttons.sendDocument")}<input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" hidden onChange={(e) => handleChangeDocuments(e, number)} /></Button>
    </Stack>
  );

  const messageLayout = (number, valueDefault = "") => (
    <Stack className={`stackMessage${number} ${classes.card}`} key={`stackMessage${number}`}>
      <DeleteCardButton onClick={() => deleteElementsTypeOne(number, "message")} />
      <CardTitle icon={MessageSquareText}>{i18n.t("flowBuilderModals.singleBlockModal.elements.text")}</CardTitle>
      <TextField label={i18n.t("flowBuilderModals.singleBlockModal.fields.message")} defaultValue={valueDefault} multiline minRows={7} className={`message${number}`} name="text" variant="outlined" margin="dense" style={{ width: "100%" }} />
    </Stack>
  );

  const intervalLayout = (number, valueDefault = 0) => (
    <Stack className={`stackInterval${number} ${classes.card}`} key={`stackInterval${number}`}>
      <DeleteCardButton onClick={() => deleteElementsTypeOne(number, "interval")} />
      <CardTitle icon={Clock3}>{i18n.t("flowBuilderModals.singleBlockModal.elements.interval")}</CardTitle>
      <TextField label={i18n.t("flowBuilderModals.singleBlockModal.fields.timeInSeconds")} className={`interval${number}`} defaultValue={valueDefault} type="number" InputProps={{ inputProps: { min: 0, max: 120 } }} variant="outlined" margin="dense" style={{ width: "100%" }} />
    </Stack>
  );

  useEffect(() => {
    const localVariables = localStorage.getItem("variables");
    if (localVariables) {
      setVariables(JSON.parse(localVariables));
    }

    if (open === "edit") {
      setLabels({ title: i18n.t("flowBuilderModals.singleBlockModal.titleEdit"), btn: i18n.t("flowBuilderModals.singleBlockModal.buttonSave") });
      setElementsSeq(data.data.seq);
      setElementsSeqEdit(data.data.seq);
      setElementsEdit(data.data.elements);
      setTimeoutSeconds(data.data.timeoutSeconds || 0);
      setAiTimeoutSeconds(data.data.aiTimeoutSeconds || 0);
      if (data) {
        const elementsEditLoc = data.data.elements;
        const sequence = data.data.seq;
        sequence.map((item) => {
          const itemNode = elementsEditLoc.filter((inode) => inode.number === item)[0];
          if (itemNode.type === "message") {
            const numberLoc = parseInt(item.replace("message", ""));
            setElements((elm) => [...elm, messageLayout(numberLoc, itemNode.value)]);
            setNumberMessages((old) => {
              const arsOnly = sequence.filter((item) => item.includes("message"));
              const arrNumberMax = arsOnly.map((item) => parseInt(item.replace("message", "")));
              setNumberMessagesLast(Math.max.apply(null, arrNumberMax) + 1);
              return old + 1;
            });
          }
          if (itemNode.type === "interval") {
            const numberLoc = parseInt(item.replace("interval", ""));
            setElements((elm) => [...elm, intervalLayout(numberLoc, itemNode.value)]);
            setNumberInterval((old) => {
              const arsOnly = sequence.filter((item) => item.includes("interval"));
              const arrNumberMax = arsOnly.map((item) => parseInt(item.replace("interval", "")));
              setNumberIntervalLast(Math.max.apply(null, arrNumberMax) + 1);
              return old + 1;
            });
          }
          if (itemNode.type === "audio") {
            const numberLoc = parseInt(item.replace("audio", ""));
            setElements((elm) => [...elm, audioLayout(numberLoc, itemNode.value, itemNode.record ? "" : "ok")]);
            setNumberAudio((old) => {
              const arsOnly = sequence.filter((item) => item.includes("audio"));
              const arrNumberMax = arsOnly.map((item) => parseInt(item.replace("audio", "")));
              setNumberAudioLast(Math.max.apply(null, arrNumberMax) + 1);
              return old + 1;
            });
          }
          if (itemNode.type === "img") {
            const numberLoc = parseInt(item.replace("img", ""));
            setElements((elm) => [...elm, imgLayout(numberLoc, itemNode.value)]);
            setNumberImg((old) => {
              const arsOnly = sequence.filter((item) => item.includes("img"));
              const arrNumberMax = arsOnly.map((item) => parseInt(item.replace("img", "")));
              setNumberImgLast(Math.max.apply(null, arrNumberMax) + 1);
              return old + 1;
            });
          }
          if (itemNode.type === "video") {
            const numberLoc = parseInt(item.replace("video", ""));
            setElements((elm) => [...elm, videoLayout(numberLoc, itemNode.value)]);
            setNumberVideo((old) => {
              const arsOnly = sequence.filter((item) => item.includes("video"));
              const arrNumberMax = arsOnly.map((item) => parseInt(item.replace("video", "")));
              setNumberVideoLast(Math.max.apply(null, arrNumberMax) + 1);
              return old + 1;
            });
          }
          if (itemNode.type === "document") {
            const numberLoc = parseInt(item.replace("document", ""));
            setElements((elm) => [...elm, documentLayout(numberLoc, itemNode.original)]);
            setNumberDocument((old) => {
              const arsOnly = sequence.filter((item) => item.includes("document"));
              const arrNumberMax = arsOnly.map((item) => parseInt(item.replace("document", "")));
              setNumberDocumentLast(Math.max.apply(null, arrNumberMax) + 1);
              return old + 1;
            });
          }
        });
      }
      setActiveModal(true);
    }
    if (open === "create") {
      setLabels({ title: i18n.t("flowBuilderModals.singleBlockModal.titleAdd"), btn: i18n.t("flowBuilderModals.singleBlockModal.buttonAdd") });
      setTimeoutSeconds(0);
      setAiTimeoutSeconds(0);
      setActiveModal(true);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = async () => {
    close(null);
    setActiveModal(false);
    setTimeout(() => {
      setMedias([]);
      setPreviewImg([]);
      setPreviewAudios([]);
      setPreviewVideos([]);
      setElements([]);
      setElementsSeq([]);
      setElementsEdit([]);
      setElementsSeqEdit([]);
      setNumberMessages(0);
      setNumberMessagesLast(0);
      setNumberInterval(0);
      setNumberIntervalLast(0);
      setNumberAudio(0);
      setNumberAudioLast(0);
      setNumberVideo(0);
      setNumberVideoLast(0);
      setNumberImg(0);
      setNumberImgLast(0);
      setNumberDocument(0);
      setNumberDocumentLast(0);
      setPreviewDocuments([]);
      setTimeoutSeconds(0);
      setAiTimeoutSeconds(0);
    }, 500);
  };
  
  const verifyButtonsUpload = () => {
    const newArrImg = elementsSeq.filter(item => item.includes("img"));
    const newArrAudio = elementsSeq.filter(item => item.includes("audio"));
    const newArrVideo = elementsSeq.filter(item => item.includes("video"));
    const newArrDocument = elementsSeq.filter(item => item.includes("document"));

    for (let i = 0; i < numberImg; i++) {
      const id = newArrImg[i].replace('img', '');
      const imgTag = document.querySelector(`.img${id}`);
      if (imgTag && !imgTag.src.includes('blob') && !imgTag.src.includes('/public/')) {
        return true;
      }
    }
    for (let i = 0; i < numberAudio; i++) {
      const id = newArrAudio[i].replace('audio', '');
      const audioDiv = document.querySelector(`.audio${id}`);
      if (audioDiv && audioDiv.innerHTML.trim() === "") {
        return true;
      }
    }
    for (let i = 0; i < numberVideo; i++) {
      const id = newArrVideo[i].replace('video', '');
      const videoDiv = document.querySelector(`.video${id}`);
      if (videoDiv && videoDiv.innerHTML.trim() === "") {
        return true;
      }
    }
    for (let i = 0; i < numberDocument; i++) {
        const id = newArrDocument[i].replace('document', '');
        const docText = document.querySelector(`.document${id}`).textContent;
        if (docText === i18n.t("flowBuilderModals.singleBlockModal.fields.noFileSelected")) {
            return true;
        }
    }
    return false;
  };
  
  const handleSaveNode = async () => {
    if (open === "edit") {
      setLoading(true);
      const formData = new FormData();
      medias.forEach(async (media, idx) => {
        const file = media;
        if (!file) return;
        if (media?.type.split("/")[0] == "image") {
          new Compressor(file, {
            quality: 0.7,
            async success(media) {
              formData.append("medias", media);
              formData.append("body", media.name);
            },
            error(err) {
              alert(i18n.t("flowBuilderModals.singleBlockModal.validation.uploadError"));
              console.log(err.message);
            },
          });
        } else {
          formData.append("medias", media);
          formData.append("body", media.name);
        }
      });
      setTimeout(async () => {
        if ((numberAudio === 0 && numberVideo === 0 && numberImg === 0 && numberDocument === 0) || medias.length === 0) {
          try {
            const mountData = { seq: elementsSeq, elements: handleElements(null), timeoutSeconds: parseInt(timeoutSeconds) || 0, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 };
            onUpdate({ ...data, data: mountData });
            toast.success(i18n.t("flowBuilderModals.singleBlockModal.messages.contentAddedSuccess"));
            handleClose();
            setLoading(false);
            return;
          } catch (e) {
            console.log(e);
            setLoading(false);
          }
          return;
        }
        const verify = verifyButtonsUpload();
        if (verify) {
          setLoading(false);
          return toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.deleteEmptyCards"));
        }
        await api.post("/flowbuilder/content", formData)
          .then(async (res) => {
            const mountData = { seq: elementsSeq, elements: handleElements(res.data), timeoutSeconds: parseInt(timeoutSeconds) || 0, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 };
            onUpdate({ ...data, data: mountData });
            toast.success(i18n.t("flowBuilderModals.singleBlockModal.messages.contentAddedSuccess"));
            await handleClose();
            setLoading(false);
          })
          .catch((error) => {
            console.log(error);
          });
      }, 1500);
    } else if (open === "create") {
      setLoading(true);
      const formData = new FormData();
      medias.forEach(async (media, idx) => {
        const file = media;
        if (!file) return;
        if (media?.type.split("/")[0] == "image") {
          new Compressor(file, {
            quality: 0.7,
            async success(media) {
              formData.append("medias", media);
              formData.append("body", media.name);
            },
            error(err) {
              alert(i18n.t("flowBuilderModals.singleBlockModal.validation.uploadError"));
              console.log(err.message);
            },
          });
        } else {
          formData.append("medias", media);
          formData.append("body", media.name);
        }
      });
      setTimeout(async () => {
        if (numberAudio === 0 && numberVideo === 0 && numberImg === 0 && numberDocument === 0) {
          try {
            const mountData = { seq: elementsSeq, elements: handleElements(null), timeoutSeconds: parseInt(timeoutSeconds) || 0, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 };
            onSave({ ...mountData });
            toast.success(i18n.t("flowBuilderModals.singleBlockModal.messages.contentAddedSuccess"));
            handleClose();
            setLoading(false);
            return;
          } catch (e) {
            setLoading(false);
          }
        }
        const verify = verifyButtonsUpload();
        if (verify) {
          setLoading(false);
          return toast.error(i18n.t("flowBuilderModals.singleBlockModal.validation.deleteEmptyCards"));
        }
        await api.post("/flowbuilder/content", formData)
          .then((res) => {
            const mountData = { seq: elementsSeq, elements: handleElements(res.data), timeoutSeconds: parseInt(timeoutSeconds) || 0, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 };
            onSave({ ...mountData });
            toast.success(i18n.t("flowBuilderModals.singleBlockModal.messages.contentAddedSuccess"));
            handleClose();
            setLoading(false);
          })
          .catch((error) => {
            console.log(error);
          });
      }, 1500);
    }
  };

  const scrollToBottom = (className) => {
    const element = document.querySelector(className);
    element.scrollTop = element.scrollHeight;
  };

  const variableFormatter = (item) => {
    return "{{" + item + "}}";
  };

  return (
    <div>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        {!loading && (
          <div className={classes.header}>
            <Typography className={classes.headerTitle}>{labels.title}</Typography>
            <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
              <CloseXIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
        )}
        <Stack>
          <DialogContent className={classes.content} style={{ display: loading ? "none" : undefined }}>
            <Stack className={`body-card ${classes.bodyCard}`}>
              {elements.map((item, index) => (<React.Fragment key={index}>{item}</React.Fragment>))}
              <div className={classes.actionGrid}>
                <Button variant="contained" className={classes.addButton} startIcon={<MessageSquareText size={16} />} onClick={() => {
                  setElements((old) => [...old, messageLayout(numberMessagesLast)]);
                  setNumberMessages((old) => { setElementsSeq((oldEleme) => [...oldEleme, `message${numberMessagesLast}`]); return old + 1; });
                  setNumberMessagesLast((old) => old + 1);
                  setTimeout(() => { scrollToBottom(".body-card"); }, 100);
                }}>{i18n.t("flowBuilderModals.singleBlockModal.elements.text")}</Button>
                <Button variant="contained" className={classes.addButton} startIcon={<Clock3 size={16} />} onClick={() => {
                  setElements((old) => [...old, intervalLayout(numberIntervalLast)]);
                  setNumberInterval((old) => { setElementsSeq((oldEleme) => [...oldEleme, `interval${numberIntervalLast}`]); return old + 1; });
                  setNumberIntervalLast((old) => old + 1);
                  setTimeout(() => { scrollToBottom(".body-card"); }, 100);
                }}>{i18n.t("flowBuilderModals.singleBlockModal.elements.interval")}</Button>
                <Button variant="contained" className={classes.addButton} startIcon={<ImageIcon size={16} />} onClick={() => {
                  setElements((old) => [...old, imgLayout(numberImgLast)]);
                  setNumberImg((old) => { setElementsSeq((oldEleme) => [...oldEleme, `img${numberImgLast}`]); return old + 1; });
                  setNumberImgLast((old) => old + 1);
                  setTimeout(() => { scrollToBottom(".body-card"); }, 100);
                }}>{i18n.t("flowBuilderModals.singleBlockModal.elements.image")}</Button>
                <Button variant="contained" className={classes.addButton} startIcon={<Mic size={16} />} onClick={() => {
                  setElements((old) => [...old, audioLayout(numberAudioLast)]);
                  setNumberAudio((old) => { setElementsSeq((oldEleme) => [...oldEleme, `audio${numberAudioLast}`]); return old + 1; });
                  setNumberAudioLast((old) => old + 1);
                  setTimeout(() => { scrollToBottom(".body-card"); }, 100);
                }}>{i18n.t("flowBuilderModals.singleBlockModal.elements.audio")}</Button>
                <Button variant="contained" className={classes.addButton} startIcon={<Video size={16} />} onClick={() => {
                  setElements((old) => [...old, videoLayout(numberVideoLast)]);
                  setNumberVideo((old) => { setElementsSeq((oldEleme) => [...oldEleme, `video${numberVideoLast}`]); return old + 1; });
                  setNumberVideoLast((old) => old + 1);
                  setTimeout(() => { scrollToBottom(".body-card"); }, 100);
                }}>{i18n.t("flowBuilderModals.singleBlockModal.elements.video")}</Button>
                <Button variant="contained" className={classes.addButton} startIcon={<FileText size={16} />} onClick={() => {
                    setElements((old) => [...old, documentLayout(numberDocumentLast)]);
                    setNumberDocument((old) => {
                      setElementsSeq((oldEleme) => [...oldEleme, `document${numberDocumentLast}`]);
                      return old + 1;
                    });
                    setNumberDocumentLast((old) => old + 1);
                    setTimeout(() => { scrollToBottom(".body-card"); }, 100);
                }}>{i18n.t("flowBuilderModals.singleBlockModal.elements.document")}</Button>
              </div>
            <Box className={classes.infoCard}>
              <div className={classes.cardHeader}>
                <span className={classes.cardIcon}><Braces size={16} /></span>
                <span>{i18n.t("flowBuilderModals.singleBlockModal.messages.variables")}</span>
              </div>
              <div className={classes.variablesList}>
                {variables && variables.map((item) => (
                  <span key={item} className={classes.variablePill}>{variableFormatter(item)}</span>
                ))}
              </div>
            </Box>
            <Stack className={classes.infoCard}>
              <CardTitle icon={TimerReset}>
                {i18n.t("flowBuilderModals.singleBlockModal.timeout.title")}
              </CardTitle>
              <TextField
                label={i18n.t("flowBuilderModals.singleBlockModal.timeout.label")}
                type="number"
                variant="outlined"
                value={timeoutSeconds}
                onChange={e => setTimeoutSeconds(e.target.value)}
                style={{ width: "100%" }}
                InputProps={{ inputProps: { min: 0 } }}
                helperText={timeoutSeconds > 0 ? i18n.t("flowBuilderModals.singleBlockModal.timeout.helperText", { seconds: timeoutSeconds }) : ""}
              />
            </Stack>

            <Stack className={classes.infoCard}>
              <CardTitle icon={Bot}>
                {i18n.t("flowBuilderModals.singleBlockModal.aiTimeout.title")}
              </CardTitle>
              <TextField
                label={i18n.t("flowBuilderModals.singleBlockModal.aiTimeout.label")}
                type="number"
                variant="outlined"
                value={aiTimeoutSeconds}
                onChange={e => setAiTimeoutSeconds(e.target.value)}
                style={{ width: "100%" }}
                InputProps={{ inputProps: { min: 0 } }}
                helperText={aiTimeoutSeconds > 0 ? i18n.t("flowBuilderModals.singleBlockModal.aiTimeout.helperText", { seconds: aiTimeoutSeconds }) : ""}
              />
            </Stack>
            </Stack>
          </DialogContent>

          <ChatiaDialogActions
            type="button"
            onClick={() => handleSaveNode()}
            saveText={`${labels.btn}`}
          />
        </Stack>
        {loading && (
          <Stack className={classes.content} style={{ gap: "8px", height: "70vh", alignSelf: "center", justifyContent: "center" }}>
            <Stack>
              <Typography>{i18n.t("flowBuilderModals.singleBlockModal.messages.uploadingFiles")}</Typography>
              <Stack style={{ alignSelf: "center", marginTop: "12px" }}><CircularProgress /></Stack>
            </Stack>
          </Stack>
        )}
      </Dialog>
    </div>
  );
};

export default FlowBuilderSingleBlockModal;
